// Anatomically-referenced layout for the Neuro-Betting brain (dual-perspective).
// Coordinate convention (brain-local):
//   x: left(-) / right(+)   y: inferior(-) / superior(+)   z: posterior(-) / anterior(+, face)

export const BRAIN_SCALE = 1.24; // monumental exhibit scale
export const BRAIN_HEIGHT = 1.4; // float at eye level (1.4m)
export const BRAIN_OFFSET = 2.15; // spacing so the larger brains never overlap

export const REGION = {
  GENERIC: 0,
  FRONTAL: 1,
  PARIETAL: 2,
  TEMPORAL: 3,
  OCCIPITAL: 4,
  ACCUMBENS: 5,
  AMYGDALA: 6,
  VTA: 7,
};

// Lobe palette taken from the anatomical diagrams:
// Blue (Frontal), Yellow (Parietal), Green (Temporal), Pink (Occipital).
// Values are tuned for legibility against the museum-white gallery.
export const LOBE_COLORS = {
  frontal: '#2f6fd0',
  parietal: '#e0a000',
  temporal: '#3fa03a',
  occipital: '#d95a90',
  limbic: '#8b98a8',
  generic: '#5a6f84',
};

const CLUSTERS = [
  { id: REGION.FRONTAL, c: [0.0, 0.14, 0.36], r: [0.40, 0.28, 0.2], w: 0.15 },
  { id: REGION.PARIETAL, c: [0.0, 0.30, -0.06], r: [0.38, 0.22, 0.24], w: 0.13 },
  { id: REGION.TEMPORAL, c: [-0.34, -0.16, 0.06], r: [0.16, 0.16, 0.28], w: 0.075 },
  { id: REGION.TEMPORAL, c: [0.34, -0.16, 0.06], r: [0.16, 0.16, 0.28], w: 0.075 },
  { id: REGION.OCCIPITAL, c: [0.0, 0.06, -0.44], r: [0.34, 0.26, 0.16], w: 0.10 },
  { id: REGION.ACCUMBENS, c: [-0.06, -0.10, 0.16], r: [0.07, 0.06, 0.08], w: 0.037 },
  { id: REGION.ACCUMBENS, c: [0.06, -0.10, 0.16], r: [0.07, 0.06, 0.08], w: 0.037 },
  { id: REGION.AMYGDALA, c: [-0.18, -0.15, 0.05], r: [0.07, 0.06, 0.08], w: 0.03 },
  { id: REGION.AMYGDALA, c: [0.18, -0.15, 0.05], r: [0.07, 0.06, 0.08], w: 0.03 },
  { id: REGION.VTA, c: [0.0, -0.20, -0.04], r: [0.05, 0.05, 0.07], w: 0.023 },
];

// Hover hotspots (invisible hit-volumes) + label anchors, in brain-local space.
export const HOTSPOTS = [
  { id: REGION.FRONTAL, name: 'Frontal Lobe', pos: [0, 0.14, 0.38], r: 0.34, color: LOBE_COLORS.frontal },
  { id: REGION.PARIETAL, name: 'Parietal Lobe', pos: [0, 0.32, -0.06], r: 0.30, color: LOBE_COLORS.parietal },
  { id: REGION.TEMPORAL, name: 'Temporal Lobe', pos: [-0.36, -0.18, 0.06], r: 0.22, color: LOBE_COLORS.temporal },
  { id: REGION.TEMPORAL, name: 'Temporal Lobe', pos: [0.36, -0.18, 0.06], r: 0.22, color: LOBE_COLORS.temporal },
  { id: REGION.OCCIPITAL, name: 'Occipital Lobe', pos: [0, 0.06, -0.46], r: 0.28, color: LOBE_COLORS.occipital },
  { id: REGION.ACCUMBENS, name: 'Nucleus Accumbens', pos: [0, -0.10, 0.16], r: 0.13, color: '#ffd700' },
  { id: REGION.AMYGDALA, name: 'Amygdala', pos: [0.18, -0.15, 0.05], r: 0.11, color: '#ff6b6b' },
  { id: REGION.VTA, name: 'VTA', pos: [0, -0.20, -0.04], r: 0.1, color: '#c9a2ff' },
];

// World-space anchor points (after scale + height) for effects & audio.
// Uses the RIGHT brain center as reference for the win pointlight; the room
// fill is centered between both brains.
export const ANCHORS = {
  center: [0, BRAIN_HEIGHT, 0],
};

function randUnitVec() {
  let x, y, z, l;
  do {
    x = Math.random() * 2 - 1;
    y = Math.random() * 2 - 1;
    z = Math.random() * 2 - 1;
    l = x * x + y * y + z * z;
  } while (l > 1 || l === 0);
  l = Math.sqrt(l);
  return [x / l, y / l, z / l];
}

function sampleEllipsoid(c, r) {
  const d = randUnitVec();
  const rad = Math.cbrt(Math.random());
  return [c[0] + d[0] * r[0] * rad, c[1] + d[1] * r[1] * rad, c[2] + d[2] * r[2] * rad];
}

const BRAIN_R = [0.6, 0.46, 0.66];

export function generateParticles(total) {
  const positions = new Float32Array(total * 3);
  const regions = new Float32Array(total);
  const seeds = new Float32Array(total);
  const MIDGAP = 0.05; // longitudinal fissure: clear channel between hemispheres

  let i = 0;
  for (const cl of CLUSTERS) {
    const n = Math.round(total * cl.w);
    for (let k = 0; k < n && i < total; k++, i++) {
      const p = sampleEllipsoid(cl.c, cl.r);
      // Open a central cleft for cortical lobes; deep limbic cores stay midline.
      if (cl.id < 4.5) p[0] += p[0] >= 0 ? MIDGAP : -MIDGAP;
      positions[i * 3] = p[0];
      positions[i * 3 + 1] = p[1];
      positions[i * 3 + 2] = p[2];
      regions[i] = cl.id;
      seeds[i] = Math.random();
    }
  }
  for (; i < total; i++) {
    const p = sampleEllipsoid([0, 0, 0], BRAIN_R);
    p[0] += p[0] >= 0 ? MIDGAP : -MIDGAP;
    positions[i * 3] = p[0];
    positions[i * 3 + 1] = p[1];
    positions[i * 3 + 2] = p[2];
    regions[i] = REGION.GENERIC;
    seeds[i] = Math.random();
  }
  return { positions, regions, seeds };
}

export function generateSynapses(positions, count) {
  const n = positions.length / 3;
  const segs = new Float32Array(count * 6);
  let w = 0;
  for (let s = 0; s < count; s++) {
    const a = (Math.random() * n) | 0;
    const ax = positions[a * 3], ay = positions[a * 3 + 1], az = positions[a * 3 + 2];
    let best = -1, bestD = 0.18 * 0.18;
    for (let t = 0; t < 12; t++) {
      const b = (Math.random() * n) | 0;
      if (b === a) continue;
      // Keep synapses within a single hemisphere so the fissure reads clearly.
      if ((positions[b * 3] < 0) !== (ax < 0)) continue;
      const dx = positions[b * 3] - ax, dy = positions[b * 3 + 1] - ay, dz = positions[b * 3 + 2] - az;
      const d = dx * dx + dy * dy + dz * dz;
      if (d < bestD) { bestD = d; best = b; }
    }
    if (best < 0) best = (Math.random() * n) | 0;
    segs[w++] = ax; segs[w++] = ay; segs[w++] = az;
    segs[w++] = positions[best * 3]; segs[w++] = positions[best * 3 + 1]; segs[w++] = positions[best * 3 + 2];
  }
  return segs;
}

// Per-brain particle count (two brains are rendered, so total is ~2x).
export function countsForQuality(quality) {
  return quality === 'low' ? 3500 : 7500;
}
